// src/components/inventory/InventoryItemsTab.js
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • Connected to FlowSense AI / ML: the statistics, the AI insight card and a new FlowSense
//     column (risk, days of cover, ML re-order point, suggested order) come from the live
//     inventory-intelligence overview (`intel` from the page, or loaded here when used alone).
//     The old ratio heuristic is only the fallback when the overview is unavailable.
//   • Live: WebSocket inventory_update / low_stock_alert / work_order_completed reload the list
//     quietly; rows that changed flash; live status pill.
//   • Search (name / SKU / location / category), filters (category, status, FlowSense risk,
//     low stock only), sort (name, stock, days of cover, value), paging.
//   • Row actions: Move stock (receive / issue / return / count / adjust / transfer) and Linked
//     assets & vehicles; low-stock "Quick reorder" opens a Receive movement.
//   • Fixes: Dismiss on an alert only re-fetched (the alert came back) — it now dismisses;
//     Export wrote unescaped CSV (a comma in a name broke the file) — FlowSense export with the
//     AI columns, escaped local CSV as the fallback; Print used REACT_APP_API_URL and crashed
//     without a QR code — print preview with copies / sizes; the print window escapes names;
//     delete shows the server message.
//   • Quick actions from the floating menu arrive as `command` (add, import, export, scan,
//     print labels).
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Button, Text, useToast, Flex, Spinner, Icon, HStack,
  Heading, useColorModeValue, SimpleGrid, Stat, StatLabel,
  StatNumber, StatHelpText, StatArrow, Badge, Tooltip
} from '@chakra-ui/react';
import { Input, InputGroup, InputLeftElement, Select, Switch, FormLabel, IconButton, Alert, AlertIcon } from '@chakra-ui/react'; // --- UPDATE
import {
  MdFileUpload, MdFileDownload, MdAdd, MdInventory,
  MdAutoGraph, MdWarning, MdAccountBalanceWallet
} from 'react-icons/md';
import { MdSearch, MdChevronLeft, MdChevronRight, MdAutoAwesome, MdQrCodeScanner } from 'react-icons/md'; // --- UPDATE
import { motion, AnimatePresence } from 'framer-motion';
import inventoryService from '../../services/inventoryService';
import LowStockAlerts from './LowStockAlerts';
import InventoryTable from './InventoryTable';
import InventoryFormModal from './InventoryFormModal';
import InventoryHistoryModal from './InventoryHistoryModal';
import ImportCSVModal from './ImportCSVModal';

// --- UPDATE: Imported the Statistics Widget ---
import InventoryStatisticsWidget from './InventoryStatisticsWidget';
// --- UPDATE (MOW+ v2)
import inventoryIntelApi from '../../services/inventoryIntelApi';
import useInventoryLive from '../../hooks/useInventoryLive';
import LiveIndicator from './LiveIndicator';
import FlowSenseInsightBar from './FlowSenseInsightBar';
import StockMovementModal from './StockMovementModal';
import PrintPreviewModal, { bulkLabelsHtml } from './PrintPreviewModal';
import { RISK_META, money } from './inventoryAiIntel';
import { config } from '../../config';
import { downloadCsv, stamp } from '../../utils/inventoryCsv';
const PAGE_SIZES = [25, 50, 100];
const RISK_ORDER = { stockout: 0, high: 1, reorder: 2, overstock: 3, dead: 4, ok: 5 };
const escHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
// --- END UPDATE

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const InventoryItemsTab = ({ intel: intelProp, lookups = null, canManage = true, hideAlerts = false, onOpenLinks = null, onCreatePO = null, onChanged = null, command = null }) => { // --- UPDATE: props from the page (all optional)
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  // --- UPDATE (MOW+ v2)
  const [ownIntel, setOwnIntel] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');
  const [risk, setRisk] = useState('all');
  const [lowOnly, setLowOnly] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [flashIds, setFlashIds] = useState(null);
  const [moving, setMoving] = useState(null);
  const [moveType, setMoveType] = useState('issue');
  const [printing, setPrinting] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const prevRef = React.useRef(null);
  const searchRef = React.useRef(null);
  const mountedRef = React.useRef(true);
  React.useEffect(() => () => { mountedRef.current = false; }, []);
  // --- END UPDATE

  const toast = useToast();

  // Theme constants for FlowSense UI
  const headerGradient = useColorModeValue('linear(to-r, blue.600, teal.400)', 'linear(to-r, blue.400, teal.300)');
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const insightText = useColorModeValue("gray.600", "gray.300"); // --- UPDATE: readable in dark mode

  // --- UPDATE: FlowSense overview — from the page, or loaded here
  const standalone = intelProp === undefined;
  const intel = standalone ? ownIntel : intelProp;
  const loadOwnIntel = useCallback(async () => {
    if (!standalone) return;
    try { const o = await inventoryIntelApi.overview(); if (mountedRef.current) setOwnIntel(o); } catch (_) { /* fallback heuristics */ }
  }, [standalone]);
  useEffect(() => { loadOwnIntel(); }, [loadOwnIntel]);
  const aiItems = intel?.items || null;
  const aiById = useMemo(() => { const m = new Map(); (aiItems || []).forEach((i) => m.set(String(i.id), i)); return m; }, [aiItems]);
  // --- END UPDATE

  // --- AI Logic: Predictive Analytics ---
  const aiInsights = useMemo(() => {
    // --- UPDATE: FlowSense AI first
    if (intel?.totals) {
      const t = intel.totals; const top = (intel.findings || [])[0];
      const color = intel.score >= 85 ? 'green' : intel.score >= 60 ? 'yellow' : 'red';
      return { msg: top ? `${top.title} — ${top.detail}` : `Health ${intel.score}/100 — ${t.below_reorder} item(s) to re-order, ${t.stockouts} out of stock.`, color, score: intel.score, findings: (intel.findings || []).length, ai: true };
    }
    // --- END UPDATE
    if (items.length === 0) return { status: 'Analyzing...', color: 'gray' };
    const lowStockRatio = alerts.length / items.length;
    if (lowStockRatio > 0.3) return { msg: 'AI Alert: Supply chain bottleneck detected. Restock highly recommended.', color: 'orange' };
    return { msg: 'AI Optimal: Stock levels are healthy for the current fulfillment cycle.', color: 'green' };
  }, [items, alerts, intel]);

  // --- Data Calculations ---
  // UPDATE: Enhanced the stats object to match what InventoryStatisticsWidget expects
  const stats = useMemo(() => {
    const totalValuation = items.reduce((acc, item) => acc + ((item.quantity || 0) * (item.unit_price || 0)), 0);
    // --- UPDATE: FlowSense numbers when available (re-order point, not only the manual threshold)
    const t = intel?.totals;
    return {
      totalItems: items.length,
      totalValue: totalValuation,
      lowStockItems: t ? t.below_reorder : alerts.length,
      needsReorder: t ? t.stockouts : alerts.filter(a => (a.quantity || 0) <= Math.floor((a.low_stock_threshold || 0) * 0.5)).length,
      recentMovements: null, // --- UPDATE: was a hard-coded 0
      stockHealth: t ? null : (items.length > 0 ? Math.round(((items.length - alerts.length) / items.length) * 100) : null), // --- UPDATE: AI score is used when present
    };
    // --- END UPDATE
  }, [items, alerts, intel]);

  const fetchData = useCallback(async (quiet = false) => { // --- UPDATE: + quiet (live refresh)
    if (!quiet) setIsLoading(true);
    try {
      const [itemsResponse, alertsResponse] = await Promise.all([
        inventoryService.getAllItems(),
        inventoryService.getLowStockAlerts().catch(() => []), // --- UPDATE: alerts are office-only — technicians still see items
      ]);
      // --- UPDATE: { data } responses + live change detection
      const list = Array.isArray(itemsResponse) ? itemsResponse : itemsResponse?.data || [];
      if (!mountedRef.current) return;
      if (quiet && prevRef.current) {
        const changed = new Set();
        list.forEach((i) => { const p = prevRef.current.get(String(i.id)); if (!p || p !== `${i.quantity}|${i.name}|${i.low_stock_threshold}|${i.warehouse_location}|${i.status}`) changed.add(String(i.id)); });
        if (changed.size && changed.size < list.length) { setFlashIds(changed); setTimeout(() => { if (mountedRef.current) setFlashIds(null); }, 2600); }
      }
      prevRef.current = new Map(list.map((i) => [String(i.id), `${i.quantity}|${i.name}|${i.low_stock_threshold}|${i.warehouse_location}|${i.status}`]));
      setItems(list || []);
      setAlerts(Array.isArray(alertsResponse) ? alertsResponse : alertsResponse?.data || []);
      setLoadError(null); live.markUpdated();
      // --- END UPDATE
    } catch (err) {
      setLoadError(err.message); // --- UPDATE
      if (!quiet) toast({ title: 'System Sync Error', description: err.message, status: 'error', isClosable: true });
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [toast]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- UPDATE: live
  const live = useInventoryLive(() => { fetchData(true); loadOwnIntel(); });
  const afterChange = useCallback(() => { fetchData(true); loadOwnIntel(); onChanged?.(); }, [fetchData, loadOwnIntel, onChanged]);
  // --- END UPDATE

  // --- Logic Enhancements ---

  const handleOpenFormModal = (item = null) => {
    setSelectedItem(item); // If item exists, this becomes an Update flow
    setIsFormModalOpen(true);
  };

  const handleExportData = () => {
    // --- UPDATE: FlowSense export (all items + AI columns, with the token); escaped local CSV as fallback
    inventoryIntelApi.download('items').then(() => toast({ title: 'Export Successful', status: 'success', duration: 2000 })).catch(() => {
    try {
      downloadCsv(`FlowSense_Export_${stamp()}.csv`, ['SKU', 'Name', 'Quantity', 'Price', 'Location'], filtered.map((i) => [i.sku, i.name, i.quantity, i.unit_price, i.warehouse_location]));
      toast({ title: 'Export Successful', status: 'success', duration: 2000 });
    } catch (e) {
      toast({ title: 'Export Failed', status: 'error' });
    }
    });
    // --- END UPDATE
  };

  const handleDelete = async (id) => {
    if (window.confirm('Enterprise Warning: Deleting this SKU will remove all associated history. Proceed?')) {
      try {
        await inventoryService.deleteItem(id);
        toast({ title: 'Record Purged', status: 'info', isClosable: true });
        afterChange(); // --- UPDATE: was fetchData — the AI overview refreshes too
      } catch (error) {
        toast({ title: 'Delete Failed', description: error?.message, status: 'error' }); // --- UPDATE
      }
    }
  };

  // --- UPDATE: dismiss really dismisses (was onDismiss={fetchData})
  const handleDismissAlert = useCallback(async (alertId) => {
    try { await inventoryService.dismissLowStockAlert(alertId); setAlerts((a) => a.filter((x) => x.id !== alertId)); }
    catch (e) { toast({ title: 'Could not dismiss', description: e?.message, status: 'error' }); }
  }, [toast]);
  const handleDismissAll = useCallback(async (ids) => { for (const id of ids) { try { await inventoryService.dismissLowStockAlert(id); } catch (_) { /* keep going */ } } fetchData(true); }, [fetchData]);
  const openMove = useCallback((item, ai, type = 'issue') => {
    const a = ai || aiById.get(String(item.id)) || null;
    setMoving({ ...item, quantity: a ? a.quantity : item.quantity, location: item.warehouse_location || a?.location || null, effective_reorder_point: a?.effective_reorder_point, suggested_order_qty: a?.suggested_order_qty });
    setMoveType(type);
  }, [aiById]);
  const reorderFromAlert = useCallback((alert) => {
    const item = items.find((i) => String(i.id) === String(alert.item_id)) || items.find((i) => i.name === alert.name);
    if (item) openMove(item, alert.insight, 'receive'); else toast({ title: 'Item not found', status: 'warning' });
  }, [items, openMove, toast]);
  // --- END UPDATE

  const handlePrint = async (item) => {
    // --- UPDATE: same API base as the rest of the app (was REACT_APP_API_URL), no QR → preview
    if (!item.qr_code_url) { setPrinting(item); return; }
    const baseUrl = config.apiUrl.replace(/\/api\/v1$/, '').replace(/\/api$/, '') || process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5000';
    // --- END UPDATE
    const qrCodeUrl = `${baseUrl}${item.qr_code_url}`;
    try {
      const response = await fetch(qrCodeUrl);
      const imageBlob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(imageBlob);
      reader.onloadend = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return; // --- UPDATE: pop-up blocked
        // --- UPDATE: escaped (a name with < or & broke / injected into the print page)
        printWindow.document.write(`
          <html><head><title>Label Print - ${escHtml(item.sku)}</title></head>
          <body style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100vh; font-family:sans-serif;">
            <div style="border:2px solid #000; padding:20px; border-radius:15px; text-align:center;">
              <h1 style="margin:0; font-size:40px;">${escHtml(item.sku)}</h1>
              <p style="font-size:20px;">${escHtml(item.name)}</p>
              <img src="${reader.result}" style="width:300px;" />
              <p>FlowSense Inventory System</p>
            </div>
            <script>window.onload = function() { window.print(); window.close(); }</script>
          </body></html>
        `);
      };
    } catch (err) {
      toast({ title: 'Print Error', status: 'error' });
    }
  };

  // --- UPDATE: search / filter / sort / paging
  const categories = useMemo(() => Array.from(new Set(items.map((i) => i.category_name).filter(Boolean))).sort(), [items]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = items.filter((i) => {
      const a = aiById.get(String(i.id));
      return (!q || [i.name, i.sku, i.warehouse_location, i.category_name, i.description].some((v) => String(v || '').toLowerCase().includes(q)))
        && (category === 'all' || (category === '__none' ? !i.category_name : i.category_name === category))
        && (status === 'all' || (i.status || 'active') === status)
        && (risk === 'all' || a?.risk === risk)
        && (!lowOnly || i.quantity <= i.low_stock_threshold || (a && ['stockout', 'high', 'reorder'].includes(a.risk)));
    });
    const val = (i) => Number(i.quantity || 0) * Number(i.unit_price || 0);
    return list.sort((x, y) => {
      if (sortBy === 'stock') return Number(x.quantity) - Number(y.quantity);
      if (sortBy === 'value') return val(y) - val(x);
      if (sortBy === 'risk') {
        const ax = aiById.get(String(x.id)); const ay = aiById.get(String(y.id));
        return (RISK_ORDER[ax?.risk] ?? 9) - (RISK_ORDER[ay?.risk] ?? 9) || (ax?.days_of_cover ?? 1e9) - (ay?.days_of_cover ?? 1e9);
      }
      return String(x.name || '').localeCompare(String(y.name || ''));
    });
  }, [items, aiById, search, category, status, risk, lowOnly, sortBy]);
  useEffect(() => { setPage(1); }, [search, category, status, risk, lowOnly, sortBy, pageSize]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);
  const riskCounts = useMemo(() => { const c = {}; (aiItems || []).forEach((i) => { c[i.risk] = (c[i.risk] || 0) + 1; }); return c; }, [aiItems]);
  const filterCount = (category !== 'all') + (status !== 'all') + (risk !== 'all') + (lowOnly ? 1 : 0) + (search ? 1 : 0);
  const printLabels = useCallback(() => {
    const list = filtered.slice(0, 200);
    if (!list.length) { toast({ title: 'No items to print', status: 'info' }); return; }
    const w = window.open('', '_blank'); if (!w) return;
    w.document.write(bulkLabelsHtml(list, config.apiUrl.replace(/\/api\/v1$/, ''))); w.document.close();
  }, [filtered, toast]);

  // quick actions from the floating menu
  useEffect(() => {
    if (!command?.type) return;
    if (command.type === 'addItem' && canManage) handleOpenFormModal();
    else if (command.type === 'importItems' && canManage) setIsImportModalOpen(true);
    else if (command.type === 'exportInventory') handleExportData();
    else if (command.type === 'printLabels') printLabels();
    else if (command.type === 'printItem' && command.item) handlePrint(command.item); // direct print of one label
    else if (command.type === 'scanBarcode') { setSearch(''); setTimeout(() => searchRef.current?.focus(), 50); toast({ title: 'Scan a barcode / QR', description: 'The scanner types into the search box — the item is found as soon as the SKU is read.', status: 'info', duration: 3500 }); }
  }, [command]); // eslint-disable-line react-hooks/exhaustive-deps
  // --- END UPDATE

  return (
    <Box p={4} data-testid="InventoryItemsTab">

      {/* UPDATE: Added Dynamic Statistics Widget */}
      <Box mb={6}>
        <InventoryStatisticsWidget stats={stats} isLoading={isLoading && !items.length} intel={intel} isRefreshing={isLoading && !!items.length} />{/* --- UPDATE: + intel */}
      </Box>

      {/* --- UPDATE: FlowSense AI bar */}
      <Box mb={6}>
        <FlowSenseInsightBar screen="items" data={intel}
          headline={intel?.totals ? [`Health ${intel.score}/100 · ${intel.totals.below_reorder} below the re-order point · ${intel.totals.stockouts} out of stock · ${money(intel.totals.reorder_value)} to re-order`, (intel.findings || [])[0] ? `Top finding: ${intel.findings[0].title}` : 'No findings — stock looks healthy.'] : `${items.length} items · FlowSense intelligence loading…`}
          questions={['What should I reorder now?', 'What will run out first?', 'Where is money tied up in dead stock?', 'Explain the inventory system for the Ministry of Works']} />
      </Box>
      {/* --- END UPDATE */}

      {/* 1. AI Insights & Valuation Bar */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
        <MotionBox whileHover={{ y: -5 }} transition={{ duration: 0.2 }}>
          <Stat p={5} bg={cardBg} borderRadius="2xl" boxShadow="sm" borderLeft="4px solid" borderColor="blue.400">
            <StatLabel color="gray.500" fontWeight="bold">TOTAL VALUATION</StatLabel>
            <StatNumber fontSize="2xl">${stats.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</StatNumber>
            <StatHelpText>{intel?.totals ? `FlowSense: ${money(intel.totals.value)} · dead stock ${money(intel.totals.dead_stock_value)}` : <><StatArrow type="increase" /> Market Rate</>}</StatHelpText>{/* --- UPDATE */}
          </Stat>
        </MotionBox>

        <MotionBox whileHover={{ y: -5 }} transition={{ duration: 0.2 }}>
          <Stat p={5} bg={cardBg} borderRadius="2xl" boxShadow="sm" borderLeft="4px solid" borderColor="orange.400">
            <StatLabel color="gray.500" fontWeight="bold">CRITICAL ALERTS</StatLabel>
            <StatNumber fontSize="2xl">{alerts.length}</StatNumber>{/* --- UPDATE: open alerts (was the low-stock count) */}
            <StatHelpText>{intel?.totals ? `${intel.totals.below_reorder} below the FlowSense re-order point` : 'SKUs below threshold'}</StatHelpText>
          </Stat>
        </MotionBox>

        <MotionBox whileHover={{ y: -5 }} transition={{ duration: 0.2 }}>
          <Flex direction="column" p={5} bg={cardBg} borderRadius="2xl" boxShadow="sm" borderLeft="4px solid" borderColor={aiInsights.color + ".400"}>
            <HStack mb={2}>
              <Icon as={aiInsights.ai ? MdAutoAwesome : MdAutoGraph} color={aiInsights.color + ".500"} />{/* --- UPDATE */}
              <Text fontWeight="bold" color="gray.500" fontSize="sm">FLOWSENSE AI INSIGHT</Text>
              {aiInsights.ai && <Badge colorScheme={aiInsights.color} ml="auto">{aiInsights.score}/100</Badge>}{/* --- UPDATE */}
            </HStack>
            <Text fontSize="xs" fontWeight="medium" color={insightText} noOfLines={3}>{aiInsights.msg || aiInsights.status}</Text>
            <Badge mt={3} colorScheme={aiInsights.color} variant="subtle" alignSelf="flex-start">{aiInsights.ai ? `${aiInsights.findings} finding(s) · FlowSense ML` : 'AI Optimized'}</Badge>{/* --- UPDATE */}
          </Flex>
        </MotionBox>
      </SimpleGrid>

      {/* 2. Enterprise Toolbar */}
      <MotionBox initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} mb={6}>
        <Flex justify="space-between" align="center" bg={cardBg} p={5} borderRadius="2xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor} gap={3} flexWrap="wrap">
          <HStack spacing={4}>
            <Box p={3} bgGradient={headerGradient} borderRadius="xl" boxShadow="lg">
              <Icon as={MdInventory} color="white" boxSize={6} />
            </Box>
            <Box>
              <Heading size="md" fontWeight="800" letterSpacing="tight">Warehouse Master</Heading>
              <HStack spacing={2}>{/* --- UPDATE: live status instead of a fixed version label */}
                <Text fontSize="xs" color="gray.400" fontWeight="bold">FLOWSENSE MOW+ · LIVE</Text>
                <LiveIndicator connected={live.connected} updatedAt={live.updatedAt} lastEvent={live.lastEvent} onRefresh={() => { fetchData(true); loadOwnIntel(); }} isRefreshing={isLoading} />
              </HStack>
            </Box>
          </HStack>

          <HStack spacing={3}>
            {canManage && ( /* --- UPDATE */
            <Tooltip label="Upload CSV Bulk Data">
              <Button leftIcon={<Icon as={MdFileUpload} />} variant="ghost" size="sm" onClick={() => setIsImportModalOpen(true)}>Import</Button>
            </Tooltip>
            )}
            <Tooltip label="Download Master Inventory Report">
              <Button leftIcon={<Icon as={MdFileDownload} />} variant="ghost" size="sm" onClick={handleExportData}>Export</Button>
            </Tooltip>
            {canManage && ( /* --- UPDATE */
            <Button
              colorScheme="blue"
              leftIcon={<Icon as={MdAdd} />}
              size="md"
              borderRadius="xl"
              onClick={() => handleOpenFormModal()}
              boxShadow="0 4px 14px 0 rgba(0,118,255,0.39)"
              _hover={{ transform: 'translateY(-2px)' }}
            >
              Add New Part
            </Button>
            )}
          </HStack>
        </Flex>
      </MotionBox>

      {/* 3. Alerts Section */}
      <AnimatePresence>
        {alerts.length > 0 && !hideAlerts && ( /* --- UPDATE: the page shows them once when hideAlerts */
          <MotionBox initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} mb={6}>
             <LowStockAlerts alerts={alerts} onDismiss={handleDismissAlert} insights={aiItems} onReorder={canManage ? reorderFromAlert : null} onCreatePO={onCreatePO} onDismissAll={handleDismissAll} />{/* --- UPDATE: really dismisses, AI insights, reorder */}
          </MotionBox>
        )}
      </AnimatePresence>

      {/* --- UPDATE: search / filters / sort */}
      <Flex bg={cardBg} p={3} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} mb={4} gap={2} flexWrap="wrap" align="center">
        <InputGroup size="sm" maxW="300px">
          <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
          <Input ref={searchRef} placeholder="Search name, SKU, location, category… (or scan)" value={search} onChange={(e) => setSearch(e.target.value)} borderRadius="lg" aria-label="Search inventory items" />
        </InputGroup>
        <Tooltip label="Scan a barcode / QR into the search"><IconButton size="sm" variant="ghost" icon={<Icon as={MdQrCodeScanner} />} aria-label="Scan barcode" onClick={() => searchRef.current?.focus()} /></Tooltip>
        <Select size="sm" maxW="170px" value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category filter" borderRadius="lg">
          <option value="all">All categories</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}<option value="__none">Uncategorized</option>
        </Select>
        <Select size="sm" maxW="150px" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status filter" borderRadius="lg">
          <option value="all">Any status</option><option value="active">Active</option><option value="discontinued">Discontinued</option><option value="obsolete">Obsolete</option>
        </Select>
        {aiItems && (
          <Select size="sm" maxW="200px" value={risk} onChange={(e) => setRisk(e.target.value)} aria-label="FlowSense risk filter" borderRadius="lg">
            <option value="all">Any FlowSense risk</option>{Object.entries(RISK_META).map(([k, m]) => <option key={k} value={k}>{m.label} ({riskCounts[k] || 0})</option>)}
          </Select>
        )}
        <HStack><Switch size="sm" id="low-only" isChecked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} colorScheme="orange" /><FormLabel htmlFor="low-only" mb={0} fontSize="xs">Low stock only</FormLabel></HStack>
        <Select size="sm" maxW="170px" value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort items" borderRadius="lg" ml="auto">
          <option value="name">Sort: name</option><option value="stock">Sort: stock (low first)</option>{aiItems && <option value="risk">Sort: FlowSense risk</option>}<option value="value">Sort: value</option>
        </Select>
        <Text fontSize="xs" color="gray.500">{filtered.length} of {items.length}</Text>
        {filterCount > 0 && <Button size="xs" variant="ghost" onClick={() => { setSearch(''); setCategory('all'); setStatus('all'); setRisk('all'); setLowOnly(false); }}>Clear</Button>}
      </Flex>
      {loadError && !items.length && <Alert status="error" borderRadius="xl" mb={4}><AlertIcon />{loadError}<Button size="sm" ml="auto" onClick={() => fetchData()}>Retry</Button></Alert>}
      {/* --- END UPDATE */}

      {/* 4. Main Data Grid */}
      <Box bg={cardBg} borderRadius="2xl" boxShadow="sm" overflow="hidden" borderWidth="1px" borderColor={borderColor}>
        {isLoading && !items.length ? ( /* --- UPDATE: live refreshes keep the table on screen */
          <Flex justify="center" align="center" h="400px" direction="column">
            <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
            <Text color="gray.500" fontWeight="bold">Syncing with Cloud Vault...</Text>
          </Flex>
        ) : (
          <InventoryTable
            items={pageItems} // --- UPDATE: filtered + paged
            onEdit={handleOpenFormModal}
            onDelete={handleDelete}
            onPrint={(item) => setPrinting(item)} // --- UPDATE: print preview (copies, sizes, no-QR labels)
            onViewHistory={(item) => {
              setSelectedItem(item);
              setIsHistoryModalOpen(true);
            }}
            // --- UPDATE
            insights={aiItems}
            flashIds={flashIds}
            onMoveStock={canManage ? openMove : null}
            onLinks={onOpenLinks}
            canManage={canManage}
            emptyText={items.length ? 'No items match the search or filters.' : null}
            // --- END UPDATE
          />
        )}
        {/* --- UPDATE: paging */}
        {filtered.length > PAGE_SIZES[0] && (
          <HStack justify="flex-end" px={4} py={2} spacing={2}>
            <Select size="xs" w="auto" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page">{PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}</Select>
            <IconButton size="sm" variant="ghost" icon={<Icon as={MdChevronLeft} />} isDisabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page" />
            <Text fontSize="xs">Page {page} / {pages}</Text>
            <IconButton size="sm" variant="ghost" icon={<Icon as={MdChevronRight} />} isDisabled={page >= pages} onClick={() => setPage((p) => p + 1)} aria-label="Next page" />
          </HStack>
        )}
      </Box>

      {/* Modals */}
      <InventoryFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        item={selectedItem}
        onSave={afterChange} // --- UPDATE: + AI overview refresh
      />

      <InventoryHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        item={selectedItem}
      />

      <ImportCSVModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportSuccess={afterChange} // --- UPDATE
      />
      {/* --- UPDATE */}
      <StockMovementModal isOpen={!!moving} onClose={() => setMoving(null)} item={moving} lookups={lookups} defaultType={moveType}
        onDone={(r) => { toast({ title: 'Stock updated', description: `${r.item_name}: ${r.quantity_before} → ${r.quantity_after}`, status: 'success', duration: 3000 }); afterChange(); }} />
      <PrintPreviewModal isOpen={!!printing} onClose={() => setPrinting(null)} item={printing} />
      {/* --- END UPDATE */}
    </Box>
  );
};

export default InventoryItemsTab;
